import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import { activityService } from '../services/activityService';
import {
  Activity,
  User,
  Clock,
  CreditCard,
  UserPlus,
  Wifi,
  WifiOff,
  Bell,
  BellOff,
  ChevronDown,
  ChevronUp,
  Filter,
  X,
  CheckCircle,
  XCircle,
  UserCog,
  Star,
  LogOut,
  Wallet,
  CalendarClock,
  Phone,
  List
} from 'lucide-react';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/Loading.json';
import { Dropdown } from './ui/dropdown-01';
import {
  shouldExcludeFromActivityFeed,
  shouldShowDetailsInActivityFeed,
} from '../utils/activityFeedExclude';
import {
  normalizeActivityData,
  parseActivityDate,
  resolveActivityTimestamp,
  resolveActivityUserId,
} from '../utils/activityPayload';
import {
  canonicalEventTypeForDisplay,
  getActivityEventPhrase,
  getActivityEventStatLabel,
  getLocationPermissionResultPhrase,
  getNotificationPermissionResultPhrase,
} from '../utils/activityEventLabels';
import {
  extractActivityEventFromSocketPayload,
  activityRowFingerprint,
  activitySemanticFingerprint,
} from '../utils/activitySocketPayload';
import {
  getScreenViewFeedPhrase,
  isScreenViewLikeEvent,
} from '../utils/activityScreenLabels';

/**
 * customer_rating_submitted: asıl kaynak eventData.requestData (veya düz requestData)
 * — point (1–5), comment, identityNum (TC)
 */
function getRatingRequestData(activityData) {
  if (!activityData || typeof activityData !== 'object') return null;
  const tryReq = (r) => {
    if (!r) return null;
    if (typeof r === 'string') {
      try {
        const p = JSON.parse(r);
        return p && typeof p === 'object' ? p : null;
      } catch {
        return null;
      }
    }
    return typeof r === 'object' ? r : null;
  };
  const candidates = [
    activityData.requestData,
    activityData.eventData?.requestData,
    activityData.eventData?.RequestData,
    activityData.EventData?.requestData,
    activityData.data?.requestData,
    activityData.data?.eventData?.requestData,
    activityData.data?.EventData?.requestData,
  ];
  for (const c of candidates) {
    const req = tryReq(c);
    if (req) return req;
  }
  return null;
}

/**
 * Müşteri puanı — önce requestData.point; yoksa düz alanlarda fallback.
 */
function getRatingScoreFromActivityData(activityData) {
  const req = getRatingRequestData(activityData);
  if (req) {
    const rawPoint =
      req.point ??
      req.Point ??
      req.POINT ??
      req.rating ??
      req.Rating;
    if (rawPoint !== undefined && rawPoint !== null && rawPoint !== '') {
      const n = Number(rawPoint);
      if (!Number.isNaN(n) && n >= 0) {
        return { score: n, max: 5 };
      }
    }
  }

  if (!activityData || typeof activityData !== 'object') return null;
  const merged = {
    ...activityData,
    ...(typeof activityData.data === 'object' && activityData.data !== null ? activityData.data : {}),
  };
  const scoreKeys = [
    'point',
    'points',
    'rating',
    'score',
    'stars',
    'star',
    'ratingValue',
    'customerRating',
    'value',
  ];
  let score = null;
  for (const k of scoreKeys) {
    const v = merged[k];
    if (v !== undefined && v !== null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n) && n >= 0) {
        score = n;
        break;
      }
    }
  }
  if (score === null) return null;
  const maxKeys = ['maxRating', 'maxPoints', 'max', 'ratingMax', 'scale', 'starsMax'];
  let max = null;
  for (const k of maxKeys) {
    const v = merged[k];
    if (v !== undefined && v !== null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n) && n > 0) {
        max = n;
        break;
      }
    }
  }
  if (max === null) max = 5;
  return { score, max };
}

function getRatingCommentText(activityData) {
  const req = getRatingRequestData(activityData);
  if (!req) return '';
  const c = req.comment ?? req.Comment;
  if (c === undefined || c === null) return '';
  return String(c).trim();
}

/** analytics:event / API data içinden kampanya adı veya id */
function getCampaignDisplayLabel(activityData) {
  if (!activityData || typeof activityData !== 'object') return null;
  const title =
    activityData.campaignTitle ||
    activityData.campaignName ||
    activityData.title ||
    activityData.name ||
    activityData.data?.campaignTitle ||
    activityData.data?.campaignName ||
    activityData.data?.title;
  if (title !== undefined && title !== null && String(title).trim() !== '') {
    const t = String(title).trim();
    return t.length > 80 ? `${t.slice(0, 77)}…` : t;
  }
  const id =
    activityData.campaignId ??
    activityData.data?.campaignId;
  if (id !== undefined && id !== null && String(id).trim() !== '') {
    return `Kampanya #${String(id).trim()}`;
  }
  return null;
}

/**
 * Event payload'undan (özellikle login eventData'sından) görünür ad çıkarır.
 * "Bilinmeyen Kullanıcı" / "Kullanıcı" gibi placeholder değerleri eler.
 */
function getNameFromActivityData(activityData) {
  if (!activityData || typeof activityData !== 'object') return null;
  const ev = activityData.eventData || activityData.EventData || {};
  const src = { ...ev, ...activityData };

  const PLACEHOLDERS = new Set([
    'bilinmeyen kullanıcı',
    'bilinmeyen',
    'kullanıcı',
    'unknown',
    'null',
    'undefined',
    '-',
  ]);

  const clean = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).normalize('NFKC').replace(/\u0307/g, '').trim();
    if (!s) return '';
    if (PLACEHOLDERS.has(s.toLocaleLowerCase('tr-TR'))) return '';
    return s;
  };

  const first = clean(src.firstName ?? src.firstname ?? src.first_name ?? src.ad);
  const last = clean(src.lastName ?? src.lastname ?? src.last_name ?? src.soyad);
  const full = `${first} ${last}`.trim();
  if (full) return full;

  const single = clean(src.fullName ?? src.full_name ?? src.userName ?? src.username ?? src.name);
  return single || null;
}

/** Limit artırma detay panelinde sonucun ötesinde faydalı bilgi var mı? */
function limitIncreaseHasUsefulDetail(result) {
  if (!result || typeof result !== 'object') return false;
  const filled = (v) => v !== null && v !== undefined && String(v).trim() !== '';
  // Detay panelinde yalnızca Durum ve Yeni Limit gösterilir; durum başlıkta zaten var
  return filled(result.newLimit);
}

function resolveLimitIncreaseOutcome(activityData) {
  const result = activityData?.limitIncreaseResult;
  if (!result || typeof result !== 'object') return null;

  if (typeof result.success === 'boolean') return result.success;

  const status = String(result.statusText || result.result || result.message || '')
    .trim()
    .toLocaleLowerCase('tr-TR');

  if (!status) return null;
  if (status.includes('onay') || status.includes('kabul') || status.includes('olumlu') || status.includes('başarılı')) {
    return true;
  }
  if (status.includes('red') || status.includes('olumsuz') || status.includes('başarısız')) {
    return false;
  }
  return null;
}

function resolveOnlinePaymentOutcome(activityData) {
  if (!activityData || typeof activityData !== 'object') return null;

  const candidates = [
    activityData.success,
    activityData.isSuccess,
    activityData.status,
    activityData.result,
    activityData.paymentStatus,
    activityData.eventData?.success,
    activityData.eventData?.status,
    activityData.eventData?.result,
    activityData.data?.success,
    activityData.data?.status,
    activityData.data?.result,
  ];

  for (const raw of candidates) {
    if (typeof raw === 'boolean') return raw;
    const status = String(raw || '').trim().toLocaleLowerCase('tr-TR');
    if (!status) continue;
    if (
      ['success', 'successful', 'succeeded', 'completed', 'paid', 'approved', 'true', 'ok', '1', 'başarılı'].includes(status)
      || status.includes('başarı')
      || status.includes('onay')
    ) {
      return true;
    }
    if (
      ['fail', 'failed', 'failure', 'error', 'declined', 'rejected', 'false', '0', 'başarısız', 'cancelled', 'canceled'].includes(status)
      || status.includes('başarısız')
      || status.includes('red')
      || status.includes('hata')
    ) {
      return false;
    }
  }

  return null;
}

function activityMatchesDateRange(activity, dateFrom, dateTo) {
  if (!dateFrom && !dateTo) return true;
  const d = parseActivityDate(
    resolveActivityTimestamp(activity) || activity?.timestamp || activity?.createdAt,
  );
  if (!d) return true;

  if (dateFrom) {
    const from = new Date(dateFrom);
    if (!Number.isNaN(from.getTime())) {
      from.setHours(0, 0, 0, 0);
      if (d < from) return false;
    }
  }
  if (dateTo) {
    const to = new Date(dateTo);
    if (!Number.isNaN(to.getTime())) {
      to.setHours(23, 59, 59, 999);
      if (d > to) return false;
    }
  }
  return true;
}

const ActivityFeed = ({ dateFrom = null, dateTo = null }) => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [displayedActivities, setDisplayedActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showLimit, setShowLimit] = useState(20); // İlk başta 20 aktivite göster
  const [filterType, setFilterType] = useState('all'); // Filtre: all, auth, user, notification, etc.
  const { socket, isConnected } = useSocket();
  const hadConnectedOnceRef = useRef(false);

  const { users } = useSelector((state) => state.users);

  /**
   * userId → görünür ad haritası.
   * Aynı kullanıcının adlı bir event'i (örn. login firstName taşır) varsa,
   * aynı kişinin isimsiz event'leri (user_registered, password_set_success,
   * user_login...) de bu ismi kullansın. Liste dışı / yeni kullanıcılar için kritik.
   */
  const userNameById = useMemo(() => {
    const map = {};
    (activities || []).forEach((a) => {
      const id = resolveActivityUserId({ ...a, data: a.data }) || a.userId;
      if (!id || map[id]) return;
      // İsim event payload'unda ya da backend'in satıra eklediği firstName/lastName alanlarında olabilir
      let name =
        getNameFromActivityData(a.data) ||
        getNameFromActivityData({ firstName: a.firstName, lastName: a.lastName });
      if (!name) {
        const u = users.find(
          (x) => String(x.tc) === String(id) || String(x.id) === String(id)
        );
        if (u && u.name && u.name !== '-') name = u.name;
      }
      if (name) map[id] = name;
    });
    return map;
  }, [activities, users]);

  /**
   * Aktivite verisinden kullanıcı kaydını bul
   */
  const getUserRecord = (userId, activityData = null) => {
    if (activityData) {
      const tcNo =
        activityData.requestData?.identityNum ||
        activityData.requestData?.tcNo ||
        activityData.eventData?.requestData?.identityNum ||
        activityData.eventData?.requestData?.tcNo ||
        activityData.data?.requestData?.identityNum ||
        activityData.tcNo ||
        activityData.identityNum ||
        activityData.eventData?.identityNum ||
        activityData.eventData?.tcNo ||
        activityData.userId;
      if (tcNo) {
        const user = users.find(u => 
          String(u.tc) === String(tcNo) || 
          String(u.id) === String(tcNo)
        );
        if (user) return user;
      }
    }
    
    // userId varsa Redux store'dan kullanıcıyı bul
    if (userId) {
      const user = users.find(u => 
        u.id === userId || 
        u.tc === userId || 
        String(u.id) === String(userId) ||
        String(u.tc) === String(userId)
      );
      if (user) return user;
    }
    
    return null;
  };

  /**
   * deviceId → userId haritası.
   * Kayıt/giriş öncesi event'ler userId taşımaz, yalnızca deviceId taşır. Aynı cihazdan
   * sonradan kimlikli bir event gelirse (kayıt olup giriş yapınca) önceki satırlar da o
   * kullanıcıya bağlanır. Cihazda birden fazla kullanıcı görüldüyse eşleştirme yapılmaz.
   */
  const userIdByDevice = useMemo(() => {
    const map = {};
    const ambiguous = new Set();
    (activities || []).forEach((a) => {
      if (!a.deviceId || ambiguous.has(a.deviceId)) return;
      const id = resolveActivityUserId({ ...a, data: a.data }) || a.userId;
      if (!id) return;
      if (map[a.deviceId] && String(map[a.deviceId]) !== String(id)) {
        ambiguous.add(a.deviceId);
        delete map[a.deviceId];
        return;
      }
      map[a.deviceId] = id;
    });
    return map;
  }, [activities]);

  const getUserName = (userId, activityData = null) => {
    const user = getUserRecord(userId, activityData);
    if (user && user.name && user.name !== '-') {
      return formatUserName(user.name);
    }
    // Redux listesinde yoksa: önce bu event'in kendi payload'undan,
    // sonra aynı userId'li başka bir adlı event'ten (harita) ismi çöz.
    const fromData = getNameFromActivityData(activityData);
    if (fromData) return formatUserName(fromData);
    if (userId && userNameById[userId]) return formatUserName(userNameById[userId]);
    return null;
  };

  /**
   * Kullanıcı adını formatla: Tam adı döndür, baş harfleri büyük yap
   */
  const formatUserName = (fullName) => {
    if (!fullName || fullName === '-') return null;

    // Bazı kaynaklarda "i̇" (i + combining dot) geliyor; normalize ederek temizle.
    const normalizedName = String(fullName)
      .normalize('NFKC')
      .replace(/\u0307/g, '')
      .trim();

    // Her kelimenin baş harfini Türkçe locale ile büyük yap.
    const formatted = normalizedName
      .split(' ')
      .map(word => {
        if (!word) return '';
        return word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR');
      })
      .join(' ');
    
    return formatted;
  };

  /**
   * Event tipini Türkçe'ye çevir (kullanıcı adı ile birlikte)
   */
  const translateEventType = (eventType, userId = null, activityData = null, includeUserPrefix = true) => {
    const userName = getUserName(userId, activityData);
    const userPrefix = includeUserPrefix && userName ? `${userName} ` : '';
    
    const lookupKey = canonicalEventTypeForDisplay(eventType);

    if (lookupKey === 'limit_increase_requested') {
      const outcome = resolveLimitIncreaseOutcome(activityData);
      if (outcome === true) {
        return `${userPrefix}limit artırma talebi onaylandı`;
      }
      if (outcome === false) {
        return `${userPrefix}limit artırma talebi reddedildi`;
      }
      return `${userPrefix}limit artırma talebinde bulundu`;
    }

    if (lookupKey === 'sms_verification_result') {
      const status = String(
        activityData?.status ?? activityData?.eventData?.status ?? activityData?.data?.status ?? ''
      ).trim().toLowerCase();
      if (status === 'success' || status === 'başarılı' || status === 'true') {
        return `${userPrefix}SMS doğrulamasını tamamladı`;
      }
      if (status === 'fail' || status === 'failed' || status === 'error' || status === 'başarısız' || status === 'false') {
        return `${userPrefix}SMS doğrulaması başarısız oldu`;
      }
      return `${userPrefix}SMS doğrulaması yaptı`;
    }

    if (lookupKey === 'notification_permission_result') {
      return `${userPrefix}${getNotificationPermissionResultPhrase(activityData)}`;
    }

    if (lookupKey === 'location_permission_result') {
      return `${userPrefix}${getLocationPermissionResultPhrase(activityData)}`;
    }

    if (lookupKey === 'online_payment_result') {
      const outcome = resolveOnlinePaymentOutcome(activityData);
      if (outcome === true) {
        return `${userPrefix}çevrimiçi ödemeyi tamamladı`;
      }
      if (outcome === false) {
        return `${userPrefix}çevrimiçi ödeme başarısız oldu`;
      }
      return `${userPrefix}çevrimiçi ödeme sonucu aldı`;
    }

    if (isScreenViewLikeEvent(eventType, activityData)) {
      return `${userPrefix}${getScreenViewFeedPhrase(activityData, eventType)}`;
    }

    if (lookupKey === 'campaign_viewed' || lookupKey === 'campaign_clicked') {
      const camp = getCampaignDisplayLabel(activityData);
      if (camp) {
        return lookupKey === 'campaign_viewed'
          ? `${userPrefix}${camp} kampanyasını görüntüledi`
          : `${userPrefix}${camp} kampanyasına tıkladı`;
      }
    }

    if (lookupKey === 'customer_rating_submitted') {
      const r = getRatingScoreFromActivityData(activityData);
      if (r) {
        const s = r.score % 1 === 0 ? String(r.score) : String(Math.round(r.score * 10) / 10);
        return `${userPrefix}uygulamaya ${s} puan verdi`;
      }
      return `${userPrefix}uygulamaya puan verdi`;
    }

    if (lookupKey === 'limit_increase_confirmed' || lookupKey === 'limit_increase_result') {
      const outcome = resolveLimitIncreaseOutcome(activityData);
      if (outcome === true) {
        return `${userPrefix}limit artırma talebi onaylandı`;
      }
      if (outcome === false) {
        return `${userPrefix}limit artırma talebi reddedildi`;
      }
      return `${userPrefix}${getActivityEventPhrase(lookupKey) || 'limit artırma sonucunu aldı'}`;
    }

    if (lookupKey === 'online_payment_3ds_result') {
      const outcome = resolveOnlinePaymentOutcome(activityData);
      if (outcome === true) {
        return `${userPrefix}3D Secure ödemeyi tamamladı`;
      }
      if (outcome === false) {
        return `${userPrefix}3D Secure ödeme başarısız oldu`;
      }
      return `${userPrefix}3D Secure ödeme sonucunu aldı`;
    }

    const phrase = getActivityEventPhrase(lookupKey);
    if (phrase) {
      return `${userPrefix}${phrase}`;
    }

    const statLabel = getActivityEventStatLabel(lookupKey);
    if (statLabel && statLabel !== lookupKey && statLabel !== eventType) {
      return `${userPrefix}${statLabel.charAt(0).toLocaleLowerCase('tr-TR')}${statLabel.slice(1)}`;
    }

    return userPrefix ? `${userPrefix}${eventType}` : eventType;
  };

  /**
   * Event tipine göre ikon döndür
   */
  const getEventIcon = (eventType, activityData = null) => {
    const key = canonicalEventTypeForDisplay(eventType);

    if (key === 'limit_increase_confirmed' || key === 'limit_increase_requested') {
      const outcome = resolveLimitIncreaseOutcome(activityData);
      if (outcome === true) return <CheckCircle className="w-5 h-5 text-green-600" />;
      if (outcome === false) return <XCircle className="w-5 h-5 text-red-600" />;
      return <CreditCard className="w-5 h-5 text-yellow-600" />;
    }

    if (key === 'online_payment_result') {
      const outcome = resolveOnlinePaymentOutcome(activityData);
      if (outcome === true) return <CheckCircle className="w-5 h-5 text-green-600" />;
      if (outcome === false) return <XCircle className="w-5 h-5 text-red-600" />;
      return <Wallet className="w-5 h-5 text-emerald-600" />;
    }

    const iconMap = {
      'user_token_registered': <Bell className="w-5 h-5 text-blue-600" />,
      'user_token_unregistered': <BellOff className="w-5 h-5 text-gray-600" />,
      'user_register': <UserPlus className="w-5 h-5 text-green-600" />,
      'user_login': <User className="w-5 h-5 text-indigo-600" />,
      login_option_click: <User className="w-5 h-5 text-indigo-600" />,
      'user_connection_established': <Wifi className="w-5 h-5 text-green-600" />,
      'user_connection_closed': <WifiOff className="w-5 h-5 text-red-600" />,
      'notification_sent': <Bell className="w-5 h-5 text-purple-600" />,
      'notification_read': <Bell className="w-5 h-5 text-blue-600" />,
      'campaign_viewed': <Activity className="w-5 h-5 text-orange-600" />,
      'campaign_clicked': <CreditCard className="w-5 h-5 text-pink-600" />,
      'limit_increase_requested': <CreditCard className="w-5 h-5 text-yellow-600" />,
      'limit_increase_approved': <CheckCircle className="w-5 h-5 text-green-600" />,
      'limit_increase_rejected': <XCircle className="w-5 h-5 text-red-600" />,
      session_update: <Clock className="w-5 h-5 text-slate-600" />,
      profile_update: <UserCog className="w-5 h-5 text-teal-600" />,
      customer_rating_submitted: <Star className="w-5 h-5 text-amber-500 fill-amber-400" />,
      user_logout: <LogOut className="w-5 h-5 text-gray-600" />,
      upcoming_installments_view: <CalendarClock className="w-5 h-5 text-violet-600" />,
      upcoming_installments_detail_click: <CalendarClock className="w-5 h-5 text-violet-600" />,
      transactions_view_all_click: <List className="w-5 h-5 text-slate-600" />,
      transactions_filter_apply: <List className="w-5 h-5 text-slate-600" />,
      contact_action_click: <Phone className="w-5 h-5 text-blue-600" />,
      payment_modal_open: <Wallet className="w-5 h-5 text-emerald-600" />,
      payment_method_selected: <Wallet className="w-5 h-5 text-emerald-600" />,
      online_payment_started: <Wallet className="w-5 h-5 text-emerald-600" />,
      online_payment_submitted: <Wallet className="w-5 h-5 text-emerald-600" />,
      sms_resend_click: <Phone className="w-5 h-5 text-blue-600" />,
    };
    return iconMap[key] || <Activity className="w-5 h-5 text-gray-600" />;
  };

  /**
   * Event tipine göre arka plan rengi döndür
   */
  const getEventBackground = (eventType, activityData = null) => {
    const key = canonicalEventTypeForDisplay(eventType);

    if (key === 'limit_increase_confirmed' || key === 'limit_increase_requested') {
      const outcome = resolveLimitIncreaseOutcome(activityData);
      if (outcome === true) return 'bg-green-50';
      if (outcome === false) return 'bg-red-50';
      return 'bg-yellow-50';
    }

    if (key === 'online_payment_result') {
      const outcome = resolveOnlinePaymentOutcome(activityData);
      if (outcome === true) return 'bg-green-50';
      if (outcome === false) return 'bg-red-50';
      return 'bg-emerald-50';
    }

    const bgMap = {
      'user_token_registered': 'bg-blue-50',
      'user_token_unregistered': 'bg-gray-50',
      'user_register': 'bg-green-50',
      'user_login': 'bg-indigo-50',
      login_option_click: 'bg-indigo-50',
      'user_connection_established': 'bg-green-50',
      'user_connection_closed': 'bg-red-50',
      'notification_sent': 'bg-purple-50',
      'notification_read': 'bg-blue-50',
      'campaign_viewed': 'bg-orange-50',
      'campaign_clicked': 'bg-pink-50',
      'limit_increase_requested': 'bg-yellow-50',
      'limit_increase_approved': 'bg-green-50',
      'limit_increase_rejected': 'bg-red-50',
      'screen_view': 'bg-cyan-50',
      session_update: 'bg-slate-50',
      profile_update: 'bg-teal-50',
      customer_rating_submitted: 'bg-amber-50',
      user_logout: 'bg-gray-50',
      upcoming_installments_view: 'bg-violet-50',
      upcoming_installments_detail_click: 'bg-violet-50',
      transactions_view_all_click: 'bg-slate-50',
      transactions_filter_apply: 'bg-slate-50',
      contact_action_click: 'bg-blue-50',
      payment_modal_open: 'bg-emerald-50',
      payment_method_selected: 'bg-emerald-50',
      online_payment_started: 'bg-emerald-50',
      online_payment_submitted: 'bg-emerald-50',
      sms_resend_click: 'bg-blue-50',
    };
    return bgMap[key] || 'bg-gray-50';
  };

  const mergeActivityLists = useCallback((prev, incoming, source = 'unknown') => {
    if (!incoming.length) return prev;
    const seenExact = new Set(prev.map((a) => activityRowFingerprint(a)));
    const seenSemantic = new Set(prev.map((a) => activitySemanticFingerprint(a)));
    const dedupedIncoming = incoming.filter((a) => {
      const exactKey = activityRowFingerprint(a);
      const semanticKey = activitySemanticFingerprint(a);
      const isDup = seenExact.has(exactKey) || seenSemantic.has(semanticKey);
      if (!isDup) {
        seenExact.add(exactKey);
        seenSemantic.add(semanticKey);
      }
      return !isDup;
    });
    if (!prev.length) {
      const initial = [...dedupedIncoming];
      initial.sort(
        (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
      );
      return initial.slice(0, 100);
    }
    const fresh = dedupedIncoming;
    if (!fresh.length) return prev;

    const merged = [...fresh, ...prev];
    merged.sort(
      (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
    );
    return merged.slice(0, 100);
  }, []);

  /**
   * Aktivite listesini getir
   * @param {{ silent?: boolean }} options — silent: periyodik yenilemede loading gösterme
   */
  const fetchActivities = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const result = await activityService.getActivities({
        limit: 50,
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
      });
      
      if (result.success) {
        const fromApi = (result.data || [])
          .map((row) => {
            const ts = resolveActivityTimestamp(row);
            const data = normalizeActivityData(row);
            const userId = resolveActivityUserId({ ...row, data }) || row.userId;
            return {
              ...row,
              userId,
              timestamp: ts || row.timestamp,
              data,
            };
          })
          .filter((a) => !shouldExcludeFromActivityFeed(a.eventType, a.data));

        setActivities((prev) => mergeActivityLists(prev, fromApi, 'api:/activities'));
      } else if (result.error) {
        setActivities((prev) => {
          if (prev.length === 0 && !silent) {
            setError(result.error || 'Aktiviteler yüklenemedi');
          }
          return prev;
        });
      }
    } catch (err) {
      setActivities((prev) => {
        if (prev.length === 0 && !silent) {
          setError('Aktiviteler yüklenirken bir hata oluştu');
        }
        return prev;
      });
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [mergeActivityLists, dateFrom, dateTo]);

  /**
   * WebSocket'ten gelen aktivite — tek canlı kaynak (Redux recentActivities kullanılmıyor)
   */
  const appendActivity = useCallback((event, source = 'socket:unknown') => {
    if (!event?.eventType) return;
    if (shouldExcludeFromActivityFeed(event.eventType, normalizeActivityData(event))) {
      return;
    }

    const ts = resolveActivityTimestamp(event) || event.timestamp || new Date().toISOString();
    const data = normalizeActivityData(event);
    const userId = resolveActivityUserId({ ...event, data }) || event.userId;
    const newActivity = {
      id: event.id || event.activityId || `activity_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      userId,
      eventType: event.eventType,
      eventCategory: event.eventCategory,
      deviceId: event.deviceId,
      firstName: event.firstName,
      lastName: event.lastName,
      timestamp: ts,
      data,
    };

    setActivities((prev) => mergeActivityLists(prev, [newActivity], source));
  }, [mergeActivityLists]);

  const handleSocketPayload = useCallback((payload, source) => {
    const event = extractActivityEventFromSocketPayload(payload);
    if (event) {
      appendActivity(event, source);
    }
  }, [appendActivity]);

  const handleActivityUpdate = useCallback((data) => {
    handleSocketPayload(data, 'socket:activity-update');
  }, [handleSocketPayload]);

  const handleAnalyticsEvent = useCallback((eventData) => {
    handleSocketPayload(eventData, 'socket:analytics:event');
  }, [handleSocketPayload]);

  /**
   * İlk yüklemede geçmiş (DB); canlı akış WebSocket.
   * Her mount'ta bir kez API merge — geri dönüşte liste eksik kalmasın.
   */
  useEffect(() => {
    setActivities([]);
    setShowLimit(20);
    fetchActivities();

    if (socket) {
      socket.on('activity-update', handleActivityUpdate);
      socket.on('analytics:event', handleAnalyticsEvent);

      return () => {
        socket.off('activity-update', handleActivityUpdate);
        socket.off('analytics:event', handleAnalyticsEvent);
      };
    }
  }, [socket, fetchActivities, handleActivityUpdate, handleAnalyticsEvent, dateFrom, dateTo]);

  /** WS yeniden bağlanınca kopma aralığındaki kayıtları DB'den tamamla */
  useEffect(() => {
    if (!isConnected) return;
    if (hadConnectedOnceRef.current) {
      fetchActivities({ silent: true });
    }
    hadConnectedOnceRef.current = true;
  }, [isConnected, fetchActivities]);

  /**
   * Sessiz REST poll.
   * Backend yalnızca KPI etkileyen olayları WS ile yayar; diğer olaylar
   * (taksit, işlemler, ödeme, limit...) yalnızca DB'ye yazılır. Bu poll
   * onları yakalar. WS bağlıyken 25 sn, kopukken 30 sn.
   */
  useEffect(() => {
    const intervalMs = isConnected ? 25000 : 30000;
    const pollId = setInterval(() => {
      fetchActivities({ silent: true });
    }, intervalMs);
    return () => clearInterval(pollId);
  }, [isConnected, fetchActivities]);

  /**
   * Zamanı formatla
   */
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Bilinmiyor';

    try {
      const date = parseActivityDate(timestamp);
      if (!date) return 'Bilinmiyor';
      if (isNaN(date.getTime())) return 'Bilinmiyor';
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      if (!Number.isFinite(diffInSeconds)) return 'Bilinmiyor';

      if (diffInSeconds < 0) {
        return date.toLocaleString('tr-TR', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
      if (diffInSeconds < 60) return 'Az önce';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} dk önce`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} saat önce`;
      
      return date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Geçersiz tarih';
    }
  };

  /**
   * Aktivite verilerini formatla ve önemli bilgileri çıkar
   */
  const formatActivityData = (data) => {
    if (!data || typeof data !== 'object') return null;

    const importantFields = ['method', 'path', 'statusCode', 'eventType', 'action'];
    const formatted = {};

    // screen_view için sayfa bilgisini ekle
    if (data.eventType === 'screen_view' || data.eventType === 'screenView') {
      const screenName = data.screenName || data.screen_name || data.page || data.route || data.path;
      if (screenName) {
        formatted.screenName = screenName;
      }
    }

    // Önemli alanları çıkar
    importantFields.forEach(field => {
      if (data[field] !== undefined) {
        formatted[field] = data[field];
      }
    });

    // limitIncreaseResult varsa ekle (öncelikli)
    if (data.limitIncreaseResult) {
      formatted.limitIncreaseResult = data.limitIncreaseResult;
    }

    // requestData veya responseData varsa özet göster
    if (data.requestData) {
      formatted.requestData = {
        ...data.requestData,
        // Hassas bilgileri gizle
        password: data.requestData.password ? '***REDACTED***' : undefined,
        token: data.requestData.token ? '***REDACTED***' : undefined,
      };
    }

    if (data.responseData) {
      formatted.responseData = {
        truncated: data.responseData.truncated,
        length: data.responseData.length,
      };
    }

    // limitIncreaseResult varsa veya diğer alanlar varsa döndür
    return Object.keys(formatted).length > 0 ? formatted : null;
  };

  /**
   * Filtreleme ve gösterilecek aktiviteleri güncelle
   */
  useEffect(() => {
    let filtered = activities.filter((a) => !shouldExcludeFromActivityFeed(a.eventType, a.data));
    filtered = filtered.filter((a) => activityMatchesDateRange(a, dateFrom, dateTo));

    // Event tipine göre filtrele
    if (filterType !== 'all') {
      filtered = filtered.filter((activity) => {
        if (filterType === 'auth') {
          return activity.eventType === 'user_login' || activity.eventCategory === 'auth';
        }
        if (filterType === 'user') {
          return activity.eventType?.includes('user_') || activity.eventCategory === 'user';
        }
        if (filterType === 'notification') {
          return activity.eventType?.includes('notification_') || activity.eventCategory === 'notification';
        }
        return activity.eventCategory === filterType;
      });
    }

    // İlk N aktiviteyi göster
    setDisplayedActivities(filtered.slice(0, showLimit));
  }, [activities, filterType, showLimit, dateFrom, dateTo]);

  /**
   * Detayları aç/kapa
   */
  const toggleDetails = (activityId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(activityId)) {
        newSet.delete(activityId);
      } else {
        newSet.add(activityId);
      }
      return newSet;
    });
  };

  if (loading && activities.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-center h-[300px]">
          <Lottie animationData={loadingAnimation} loop={true} className="w-32 h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
            <Activity size={20} />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Anlık Aktivite Akışı</h3>
            <p className="text-sm text-gray-500 mt-1">
              {activities.length > 0 && `${activities.length} aktivite`}
              {activities.length === 0 && 'Gerçek zamanlı kullanıcı aktiviteleri'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Filtre */}
          <Dropdown
            iconLeft={<Filter size={14} />}
            items={[
              { value: 'all', label: 'Tümü' },
              { value: 'auth', label: 'Kimlik Doğrulama' },
              { value: 'user', label: 'Kullanıcı' },
              { value: 'notification', label: 'Bildirim' },
            ]}
            value={filterType}
            onChange={setFilterType}
            simple
            className="min-w-[200px]"
            buttonClassName="py-2 text-sm"
          />
        </div>
      </div>

      {/* Sadece WebSocket verileri yoksa ve API hatası varsa hata göster */}
      {error && activities.length === 0 && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <p className="text-sm text-yellow-700">
            ⚠️ API'den veri yüklenemedi. WebSocket'ten gelen veriler gösteriliyor.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <p className="text-xs text-yellow-600 mt-1">{error}</p>
          )}
        </div>
      )}

      <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
        {displayedActivities.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Activity size={32} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm">
              {filterType !== 'all' ? 'Bu filtre için aktivite bulunamadı' : 'Henüz aktivite yok'}
            </p>
          </div>
        ) : (
          displayedActivities.map((activity, index) => {
            const isExpanded = expandedItems.has(activity.id || `activity_${activity.timestamp}`);
            const lookupEvt = canonicalEventTypeForDisplay(activity.eventType);
            const isCustomerRating = lookupEvt === 'customer_rating_submitted';
            const ratingScore = isCustomerRating ? getRatingScoreFromActivityData(activity.data) : null;
            const ratingCommentText = isCustomerRating ? getRatingCommentText(activity.data) : '';

            const formattedData = formatActivityData(activity.data);
            const limitResult = activity.data?.limitIncreaseResult;
            const hasUsefulLimitDetail = limitIncreaseHasUsefulDetail(limitResult);
            const otherFormattedKeys = formattedData
              ? Object.keys(formattedData).filter((k) => k !== 'limitIncreaseResult')
              : [];
            // limit_increase: sonuç başlıkta zaten var; detayı yalnızca ek bilgi
            // (mesaj/mevcut limit/yeni limit/sonuç) varsa göster.
            const hasDetails =
              shouldShowDetailsInActivityFeed(activity.eventType) &&
              (isCustomerRating ||
                hasUsefulLimitDetail ||
                (!limitResult && otherFormattedKeys.length > 0));

            // Unique key oluştur: id varsa kullan, yoksa timestamp + index kombinasyonu
            const uniqueKey = activity.id || `activity_${activity.timestamp}_${index}`;

            return (
              <div
                key={uniqueKey}
                className={`p-3 rounded-xl border border-gray-100 hover:shadow-sm transition-all duration-200 ${getEventBackground(activity.eventType, activity.data)}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg bg-white shadow-sm ${getEventBackground(activity.eventType, activity.data)} flex-shrink-0`}>
                    {getEventIcon(activity.eventType, activity.data)}
                  </div>
                    <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm mb-1 truncate">
                          {(() => {
                            const ownUserId =
                              resolveActivityUserId({ ...activity, data: activity.data }) ||
                              activity.userId;
                            // Kimliksiz satır: aynı cihazdan sonradan gelen kimlikli event varsa ona bağla
                            const resolvedUserId =
                              ownUserId || (activity.deviceId ? userIdByDevice[activity.deviceId] : null) || null;
                            const user = getUserRecord(resolvedUserId, activity.data);
                            const userName = getUserName(resolvedUserId, activity.data);
                            const message = translateEventType(
                              activity.eventType,
                              resolvedUserId,
                              activity.data,
                              false,
                            );
                            const routeId = user?.tc || user?.id || resolvedUserId || null;

                            if (!userName) {
                              // Henüz kayıt/giriş yapmamış kullanıcı: kimlik gelene kadar etiketle göster
                              if (!resolvedUserId) {
                                return (
                                  <>
                                    <span className="font-semibold text-gray-500">Misafir</span> {message}
                                  </>
                                );
                              }
                              return message;
                            }

                            if (!routeId) {
                              return (
                                <>
                                  <span className="font-semibold">{userName}</span> {message}
                                </>
                              );
                            }

                            return (
                              <>
                                <button
                                  type="button"
                                  onClick={() => navigate(`/kullanicilar/${routeId}`)}
                                  className="text-gray-900 hover:text-black hover:underline font-semibold"
                                >
                                  {userName}
                                </button>{' '}
                                {message}
                              </>
                            );
                          })()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                        <Clock size={12} />
                        <span>{formatTime(activity.timestamp)}</span>
                      </div>
                    </div>

                    {/* Detaylar (açılır/kapanır) */}
                    {hasDetails && (
                      <div className="mt-2">
                        <button
                          onClick={() => toggleDetails(activity.id || `activity_${activity.timestamp}`)}
                          className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp size={12} />
                              Detayları Gizle
                            </>
                          ) : (
                            <>
                              <ChevronDown size={12} />
                              Detayları Göster
                            </>
                          )}
                        </button>
                        {isExpanded && (
                          <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200">
                            {isCustomerRating ? (
                              <div className="space-y-2 text-sm">
                                <p>
                                  <span className="text-gray-500">Puan:</span>{' '}
                                  <span className="text-gray-900 font-medium">
                                    {ratingScore ? `${ratingScore.score}/${ratingScore.max}` : '—'}
                                  </span>
                                </p>
                                <p className="break-words">
                                  <span className="text-gray-500">Yorum:</span>{' '}
                                  <span className="text-gray-900 whitespace-pre-wrap">
                                    {ratingCommentText || '—'}
                                  </span>
                                </p>
                              </div>
                            ) : formattedData?.limitIncreaseResult ? (
                              <div className="space-y-2">
                                <div className="pb-2 border-b border-gray-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">Limit Artırma Sonucu</p>
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs text-gray-500">Durum:</span>
                                      <span className={`text-xs font-medium ${
                                        formattedData.limitIncreaseResult.success
                                          ? 'text-green-700'
                                          : 'text-red-700'
                                      }`}>
                                        {formattedData.limitIncreaseResult.statusText}
                                      </span>
                                    </div>
                                    {formattedData.limitIncreaseResult.newLimit !== null &&
                                      formattedData.limitIncreaseResult.newLimit !== undefined && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-500">Yeni Limit:</span>
                                        <span className="text-xs text-gray-700 font-medium">
                                          {formattedData.limitIncreaseResult.newLimit}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : formattedData && Object.keys(formattedData).length > 0 ? (
                              <pre className="text-xs text-gray-600 whitespace-pre-wrap overflow-x-auto">
                                {JSON.stringify(formattedData, null, 2)}
                              </pre>
                            ) : null}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Daha fazla göster butonu */}
      {activities.length > showLimit && (
        <div className="mt-3 text-center">
          <button
            onClick={() => setShowLimit(prev => Math.min(prev + 20, activities.length))}
            className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            {showLimit < activities.length 
              ? `${activities.length - showLimit} aktivite daha göster`
              : 'Tümünü göster'
            }
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;

